// src/components/TextToPDF.js

import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import './Dashboard.css';
import { ReactComponent as IconText } from './icons/text.svg';

const TextToPDF = () => {
  const [text, setText] = useState('');

  const handleConvert = () => {
  const doc = new jsPDF();
  // page dimensions and margins
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  // draw a border around the page
  doc.setLineWidth(0.5);
  doc.rect(margin / 2, margin / 2, pageWidth - margin, pageHeight - margin);

  // split text to fit inside the border
  const usableWidth = pageWidth - margin * 2;
  const lines = doc.splitTextToSize(text || '', usableWidth);
  // start text a bit below the top border
  doc.text(lines, margin, margin + 6);
  doc.save('text-converted.pdf');
  };

  return (
    <div>
    <div style={styles.container}>
      <h2>Convert Text to PDF</h2>
      <textarea
        style={styles.textarea}
        placeholder="Enter or paste your text here..."
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button className="tool-button" onClick={handleConvert}><IconText aria-hidden="true"/> Download PDF</button>
    </div>
     <div className="content">
      <h2>From Plain Text to Polished PDF: How the Magic Happens</h2>

      <p>
        You've likely done it a hundred times: you have a .txt file, a snippet of code,
        or just some notes in a simple text editor, and you need to turn it into a
        universal, good-looking PDF. You click "Save As PDF" or use an online
        converter, and poof—it's done. But what's actually happening behind the scenes?
      </p>

      <p>
        It’s not magic; it’s a fascinating process of translation and structuring.
        Let's demystify it.
      </p>

      <h3>The Core Idea: From Fluid Text to Fixed Layout</h3>

      <p>
        At its heart, converting text to PDF is about transforming a fluid, unstructured
        sequence of characters into a precisely formatted, fixed-layout document.
      </p>

      <p>
        <strong>Source Text:</strong> "Hello, World!" is just a sequence of characters.
        The file doesn't inherently know about fonts, page sizes, or margins.
      </p>

      <p>
        <strong>PDF:</strong> The PDF file knows exactly where to place the text
        "Hello, World!" on a specific page (e.g., 2 inches from the top, 1 inch from the
        left), what font to use (e.g., Helvetica, 12pt), and how to encode it.
      </p>

      <h3>How It Actually Works: A Step-by-Step Technical Journey</h3>
      <p>Here’s the journey your text takes from a simple string to a professional PDF document.</p>

      <h4>Step 1: Parsing and Interpreting the Text</h4>
      <p>
        The converter first reads your plain text. While it seems simple, it looks for
        basic structural cues:
      </p>
      <ul>
        <li><strong>Line Breaks:</strong> A single line break (\n) is interpreted as a space, while consecutive line breaks signal a new paragraph.</li>
        <li><strong>Tabs & Spaces:</strong> Recognized as indentation or whitespace.</li>
        <li><strong>Page Breaks:</strong> Some converters use special characters (like form feed \f) to force a new page.</li>
      </ul>

      <h4>Step 2: Applying Styling and Layout Rules</h4>
      <p>
        This is where the converter applies its default or user-defined settings:
      </p>
      <ul>
        <li>Page Size (A4, Letter, Legal)</li>
        <li>Margins (Top, Bottom, Left, Right)</li>
        <li>Font Family (Helvetica, Times New Roman, Arial)</li>
        <li>Font Size (10pt, 12pt)</li>
        <li>Line Spacing (Single, 1.5, Double)</li>
      </ul>

      <h4>Step 3: The "Flow" and Pagination Engine</h4>
      <p>
        The engine positions text on the page line by line, wrapping words as needed and
        creating new pages when reaching the bottom margin.
      </p>

      <h4>Step 4: Generating the PDF Code</h4>
      <p>
        A PDF is structured like a small program. It includes:
      </p>
      <ul>
        <li>A header (PDF version)</li>
        <li>Page objects (size/resources)</li>
        <li>Font objects (embedded/referenced)</li>
        <li>
          <strong>Content streams:</strong> Actual drawing commands, like:
          <pre>{`BT
/F1 12 Tf
72 720 Td
(Hello, World!) Tj
ET`}</pre>
        </li>
        <li>A cross-reference table</li>
        <li>A trailer</li>
      </ul>

      <h4>Step 5: Finalizing the File</h4>
      <p>
        All components—header, body, x-ref table, trailer—are written into a
        self-contained PDF file that appears uniformly on all devices.
      </p>
    </div>
    </div>
  );
};

const styles = {
  container: { padding: 20, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 6 },
  textarea: {
    width: '80%',
    height: '200px',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: 4,
  },
  button: {
    marginTop: 20,
    padding: '10px 20px',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
  },
};

export default TextToPDF;
